import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Truck, Search, ExternalLink, StickyNote, Pencil, X, Landmark, Camera, Upload } from "lucide-react";
import EditableNote from "@/components/EditableNote";
import InlineStatus from "@/components/InlineStatus";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { EMPAQUE, ENTREGA } from "@/lib/statuses";
import { useAuth, hasRole } from "@/context/AuthContext";

const labelFrom = (options, value) => options.find((o) => o.value === value)?.label || value || "—";
const colorFrom = (options, value) => options.find((o) => o.value === value)?.cls || "bg-zinc-100 text-zinc-700";

function StatusPill({ options, value }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-widest ${colorFrom(options, value)}`}>
      {labelFrom(options, value)}
    </span>
  );
}

/**
 * Compact photo uploader for finiquito / transferencia. Stores base64 via PATCH /checklist.
 */
function PhotoCell({ order, kind, initial, onSave }) {
  const [preview, setPreview] = useState(initial || "");
  const [openView, setOpenView] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => setPreview(initial || ""), [initial]);

  const pick = () => inputRef.current?.click();
  const onFile = async (f) => {
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) { toast.error("Máx 6MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = String(reader.result || "");
      setPreview(b64);
      try { await onSave(b64); toast.success("Foto guardada"); }
      catch (e) { toast.error(formatApiError(e)); setPreview(initial || ""); }
    };
    reader.readAsDataURL(f);
  };
  const label = kind === "finiquito" ? "Finiquito" : "Transferencia";
  return (
    <div className="flex flex-col items-center gap-1">
      <input ref={inputRef} type="file" accept="image/*" className="hidden"
             onChange={(e) => onFile(e.target.files?.[0])} data-testid={`upload-${kind}-${order.numero}`} />
      {preview ? (
        <button type="button" onClick={() => setOpenView(true)} className="w-10 h-10 rounded border border-emerald-400 overflow-hidden">
          <img src={preview} alt={label} className="w-full h-full object-cover" />
        </button>
      ) : (
        <button type="button" onClick={pick} className="w-10 h-10 rounded border border-dashed border-zinc-300 flex items-center justify-center hover:border-zinc-500 hover:bg-zinc-50" title={`Subir foto ${label.toLowerCase()}`}>
          <Camera className="w-4 h-4 text-zinc-400" />
        </button>
      )}
      <span className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</span>
      {preview && (
        <button type="button" onClick={pick} className="text-[9px] text-blue-600 hover:underline flex items-center gap-0.5">
          <Upload className="w-2.5 h-2.5" /> Cambiar
        </button>
      )}
      <Dialog open={openView} onOpenChange={setOpenView}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Foto de {label} · Pedido {order.numero}</DialogTitle></DialogHeader>
          {preview && <img src={preview} alt="preview" className="w-full max-h-[70vh] object-contain rounded border" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Small yes/no/empty dropdown for the checklist columns (¿está?, ¿desea cambio?).
 */
function ChecklistSelect({ value, onChange, testid }) {
  const opts = [
    { v: "", label: "—", cls: "bg-zinc-100 text-zinc-500" },
    { v: "si", label: "Sí", cls: "bg-emerald-100 text-emerald-800" },
    { v: "no", label: "No", cls: "bg-red-100 text-red-800" },
  ];
  const current = opts.find((o) => o.v === (value || "")) || opts[0];
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className={`text-[11px] font-semibold uppercase tracking-wider rounded-full px-2 py-1 border border-transparent focus:outline-none focus:ring-2 focus:ring-blue-400 ${current.cls}`}
      data-testid={testid}
    >
      {opts.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
    </select>
  );
}

/**
 * Inline text input that saves via PATCH on blur / Enter.
 */
function InlineText({ initial = "", onSave, placeholder = "—", testid, monospace = false }) {
  const [value, setValue] = useState(initial);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);
  useEffect(() => setValue(initial), [initial]);
  useEffect(() => { if (editing) ref.current?.focus(); }, [editing]);

  const commit = async () => {
    if (value === initial) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(value.trim());
    } catch (e) {
      toast.error(formatApiError(e));
      setValue(initial);
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <input
        ref={ref}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Escape") { setValue(initial); setEditing(false); }
          if (e.key === "Enter") commit();
        }}
        disabled={saving}
        placeholder={placeholder}
        className={`w-full text-xs rounded border border-blue-300 bg-blue-50 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400 ${monospace ? "font-mono" : ""}`}
        data-testid={testid}
      />
    );
  }
  if (value) {
    return (
      <button
        onClick={() => setEditing(true)}
        className={`text-left w-full text-xs bg-blue-50 border border-blue-200 rounded px-2 py-1 text-zinc-800 hover:border-blue-400 transition-colors ${monospace ? "font-mono" : ""}`}
        data-testid={`${testid}-view`}
      >
        {value}
      </button>
    );
  }
  return (
    <button
      onClick={() => setEditing(true)}
      className="text-[11px] text-zinc-400 hover:text-[#002FA7] inline-flex items-center gap-1"
      data-testid={`${testid}-add`}
    >
      <Pencil className="w-3 h-3" /> {placeholder}
    </button>
  );
}

export default function SeguimientoEnvio() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("activos");        // "activos" | "historial"
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/orders?all=true");
      // Cancelados fuera del seguimiento
      setOrders(data.filter((o) => o.estado !== "cancelado"));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const s = q.trim().toLowerCase();
  const base = tab === "historial"
    ? orders.filter((o) => o.estado_entrega === "entregado")
    : orders.filter((o) => o.estado_entrega !== "entregado");

  const filtered = base.filter((o) => {
    // filtro por fecha de entrega real (en historial)
    if (tab === "historial") {
      const ref = (o.estado_entrega_at || o.fecha_entregado || o.fecha_entrega || "").slice(0, 10);
      if (desde && ref && ref < desde) return false;
      if (hasta && ref && ref > hasta) return false;
    }
    if (!s) return true;
    return (o.numero || "").toLowerCase().includes(s) ||
      (o.cliente_nombre || "").toLowerCase().includes(s) ||
      (o.creado_por_nombre || "").toLowerCase().includes(s) ||
      (o.numero_guia || "").toLowerCase().includes(s) ||
      (o.items || []).some((it) => (it.product_name || "").toLowerCase().includes(s));
  });

  const saveGuia = async (o, valor) => {
    const { data } = await api.patch(`/orders/${o.id}/guia`, { numero_guia: valor });
    setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, numero_guia: data.numero_guia } : x));
    toast.success(valor ? "Número de guía guardado" : "Número de guía eliminado");
  };
  const saveComentario = async (o, valor) => {
    const { data } = await api.patch(`/orders/${o.id}/comentario-seguimiento`, { comentario_seguimiento: valor });
    setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, comentario_seguimiento: data.comentario_seguimiento } : x));
    toast.success("Comentario guardado");
  };
  const saveChecklist = async (o, patch) => {
    try {
      const { data } = await api.patch(`/orders/${o.id}/checklist`, patch);
      setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, ...patch } : x));
      toast.success("Actualizado");
      return data;
    } catch (e) {
      toast.error(formatApiError(e));
      throw e;
    }
  };

  return (
    <div className="space-y-6" data-testid="seguimiento-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Post-venta</div>
        <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
          <Truck className="w-8 h-8 text-[#10B981]" /> Seguimiento y Entregas (tabla)
        </h1>
        <p className="text-zinc-500 mt-2 text-sm">
          Vista unificada del estado post-empaque: comentarios internos, números de guía y estatus de empaque/entrega.
        </p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Pedidos</div>
          <div className="font-manrope font-black text-3xl mt-1">{filtered.length}</div>
        </Card>
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Con Guía</div>
          <div className="font-manrope font-black text-3xl mt-1">{filtered.filter((o) => (o.numero_guia || "").trim()).length}</div>
        </Card>
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Empacados</div>
          <div className="font-manrope font-black text-3xl mt-1">{filtered.filter((o) => (o.estado_empaque || "") === "empacado").length}</div>
        </Card>
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Entregados</div>
          <div className="font-manrope font-black text-3xl mt-1">{filtered.filter((o) => (o.estado_entrega || "") === "entregado").length}</div>
        </Card>
      </div>

      <div className="flex items-center justify-between border-b border-zinc-200">
        <div className="flex gap-1">
          <button
            onClick={() => setTab("activos")}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === "activos" ? "border-emerald-700 text-emerald-800" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}
            data-testid="tab-activos"
          >
            Pedidos activos
          </button>
          <button
            onClick={() => setTab("historial")}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === "historial" ? "border-emerald-700 text-emerald-800" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}
            data-testid="tab-historial"
          >
            Historial de entregados
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="relative max-w-md flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por pedido, cliente, vendedor, producto o guía..." className="pl-9" data-testid="seg-search" />
        </div>
        {tab === "historial" && (
          <>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Entregado desde</label>
              <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="hist-entregas-desde" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Entregado hasta</label>
              <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="hist-entregas-hasta" />
            </div>
          </>
        )}
      </div>

      <Card className="border-zinc-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr className="text-left">
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Pedido</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Vendedor</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Productos</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Notas</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Fecha entrega</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Cliente</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Empaque</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Entrega</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Comentario</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Nº de Guía</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Costo guía</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">N° de finiquito</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">N° de depósito</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Fotos</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Ubicación</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filtered.length === 0 && (
                <tr><td colSpan={16} className="text-center text-zinc-400 py-12">
                  <Truck className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
                  Sin pedidos.
                </td></tr>
              )}
              {filtered.map((o) => (
                <tr key={o.id} className="hover:bg-zinc-50 align-top" data-testid={`seg-row-${o.numero}`}>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <div className="font-mono text-xs font-semibold">{o.numero}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">{formatDate(o.fecha)}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5 capitalize">{o.envio_tipo === "ciudad" ? "Ciudad" : "Departamento"}</div>
                  </td>
                  <td className="px-3 py-3 text-xs text-zinc-700 whitespace-nowrap">
                    {o.creado_por_nombre || <span className="text-zinc-300">—</span>}
                  </td>
                  <td className="px-3 py-3 min-w-[220px] max-w-md">
                    <ul className="space-y-0.5">
                      {(o.items || []).map((it, i) => (
                        <li key={i} className="text-xs flex items-start gap-2">
                          <span className="inline-block min-w-[24px] text-right font-mono font-bold text-zinc-900">{it.cantidad}×</span>
                          <span className="flex-1">
                            <span className="font-medium">{it.product_name}</span>
                            {(it.color || it.tamano || it.estilo) && (
                              <span className="text-zinc-500"> — {[it.color, it.tamano, it.estilo].filter(Boolean).join(" · ")}</span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="px-3 py-3 max-w-[220px] min-w-[180px]">
                    <EditableNote
                      orderId={o.id}
                      modulo="notas_ent" initial={o.notas_ent ?? o.notas_hoja ?? ""}
                      onSaved={(v) => setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, notas_ent: v } : x))}
                    />
                    {o.notas && (
                      <div className="mt-1 text-[10px] text-zinc-500 italic border-l-2 border-zinc-300 pl-2">
                        <StickyNote className="w-3 h-3 inline mr-1" />{o.notas}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    {o.fecha_entrega ? (
                      <span className="text-sm font-semibold text-orange-700">{formatDate(o.fecha_entrega)}</span>
                    ) : <span className="text-zinc-300 text-xs">—</span>}
                  </td>
                  <td className="px-3 py-3 min-w-[160px]">
                    <div className="font-semibold">{o.cliente_nombre}</div>
                    {o.cliente_telefono && <div className="text-[11px] text-zinc-500">{o.cliente_telefono}{o.cliente_telefono2 ? <span className="text-zinc-400"> · alt {o.cliente_telefono2}</span> : null}</div>}
                  </td>
                  <td className="px-3 py-3">
                    <StatusPill options={EMPAQUE} value={o.estado_empaque || "pendiente"} />
                  </td>
                  <td className="px-3 py-3">
                    <InlineStatus
                      orderId={o.id}
                      campo="estado_entrega"
                      value={o.estado_entrega || "pendiente"}
                      options={ENTREGA}
                      canEdit={hasRole(user, "admin") || hasRole(user, "piloto") || hasRole(user, "facturacion")}
                      onChanged={(next) => setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, ...next } : x))}
                    />
                  </td>
                  <td className="px-3 py-3 min-w-[200px] max-w-[240px]">
                    <InlineText
                      initial={o.comentario_seguimiento || ""}
                      onSave={(v) => saveComentario(o, v)}
                      placeholder="Agregar comentario"
                      testid={`seg-comentario-${o.numero}`}
                    />
                  </td>
                  <td className="px-3 py-3 min-w-[160px]">
                    <InlineText
                      initial={o.numero_guia || ""}
                      onSave={(v) => saveGuia(o, v)}
                      placeholder="Nº de guía"
                      testid={`seg-guia-${o.numero}`}
                      monospace
                    />
                  </td>
                  <td className="px-3 py-3">
                    <CostoGuiaInline
                      order={o}
                      onSaved={(next) => setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, ...next } : x))}
                    />
                  </td>
                  <td className="px-3 py-3 min-w-[140px]">
                    <InlineText
                      initial={o.checklist_numero_finiquito || o.checklist_desea_cambio || ""}
                      onSave={(v) => saveChecklist(o, { checklist_numero_finiquito: v })}
                      placeholder="N° finiquito"
                      testid={`seg-finiquito-${o.numero}`}
                      monospace
                    />
                  </td>
                  <td className="px-3 py-3 min-w-[140px]">
                    <InlineText
                      initial={o.checklist_numero_deposito || o.checklist_banco_transferencia || ""}
                      onSave={(v) => saveChecklist(o, { checklist_numero_deposito: v })}
                      placeholder="N° depósito"
                      testid={`seg-deposito-${o.numero}`}
                      monospace
                    />
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex gap-2">
                      <PhotoCell
                        order={o}
                        kind="finiquito"
                        initial={o.checklist_foto_finiquito}
                        onSave={(b64) => saveChecklist(o, { checklist_foto_finiquito: b64 })}
                      />
                      <PhotoCell
                        order={o}
                        kind="transferencia"
                        initial={o.checklist_foto_transferencia}
                        onSave={(b64) => saveChecklist(o, { checklist_foto_transferencia: b64 })}
                      />
                    </div>
                  </td>
                  <td className="px-3 py-3 min-w-[160px]">
                    <InlineText
                      initial={o.checklist_ubicacion || ""}
                      onSave={(v) => saveChecklist(o, { checklist_ubicacion: v })}
                      placeholder="Zona / referencia"
                      testid={`seg-ubic-${o.numero}`}
                    />
                  </td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <Link to={`/pedidos/${o.id}`} className="text-[#002FA7] text-xs font-semibold uppercase tracking-widest inline-flex items-center gap-1">
                      Ver <ExternalLink className="w-3 h-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// -------- Costo de la guía (descuenta de saldo de Plataforma de Guías) ----------
function CostoGuiaInline({ order, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    costo: order.costo_guia ?? 0,
    fecha: new Date().toISOString().slice(0, 10),
  });
  const openModal = () => { setForm({ costo: order.costo_guia ?? 0, fecha: new Date().toISOString().slice(0, 10) }); setOpen(true); };
  const save = async () => {
    const num = Number(form.costo || 0);
    try {
      const { data } = await api.patch(`/orders/${order.id}/costo-guia`, { costo_guia: num, banco: "", fecha: form.fecha });
      onSaved(data);
      toast.success(num > 0 ? "Costo de guía registrado" : "Costo eliminado");
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const v = Number(order.costo_guia || 0);
  return (
    <>
      <button
        onClick={openModal}
        className={`text-xs px-2 py-1 rounded border ${v > 0 ? "bg-blue-50 border-blue-200 text-blue-900 font-semibold" : "border-dashed border-zinc-300 text-zinc-400"}`}
        data-testid={`seg-costoguia-btn-${order.numero}`}
      >
        {v > 0 ? `Q${v.toFixed(2)}` : "+ Costo"}
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-md p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid={`seg-costoguia-modal-${order.numero}`}>
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold">Costo de la guía · Pedido {order.numero}</h3>
              <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <p className="text-[11px] text-zinc-500 mb-3">Este monto se descuenta automáticamente del <b>saldo disponible de la Plataforma de Guías</b>. No afecta bancos ni cajas.</p>
            <div className="grid grid-cols-1 gap-3">
              <div><Label className="text-xs">Costo (Q) *</Label><input type="number" step="0.01" value={form.costo} onChange={(e) => setForm({ ...form, costo: Number(e.target.value) })} className="w-full h-9 border border-zinc-300 rounded px-2 text-sm" data-testid={`seg-costoguia-${order.numero}`} /></div>
              <div><Label className="text-xs">Fecha</Label><input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className="w-full h-9 border border-zinc-300 rounded px-2 text-sm" data-testid={`seg-costoguia-fecha-${order.numero}`} /></div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="text-xs px-3 py-1.5 border border-zinc-300 rounded" onClick={() => setOpen(false)}>Cancelar</button>
              <button className="text-xs px-3 py-1.5 bg-[#002FA7] text-white rounded" onClick={save} data-testid={`seg-costoguia-save-${order.numero}`}>Guardar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
